import * as angular from 'angular';
import * as _ from 'lodash';

import { DateRange, KeyValuePair, IUserModel, IUserModelDto, ApplicationUserViewModel, OnlineUserDto } from '../../models';
import { IUploadService, IRequestService } from '../../services';

interface UserDto { userName: string; roles: string; guid: string; }
export interface IPictureDictionary { [userId: string]: string; }

export interface IUserService {
    getAll(): angular.IPromise<any>;
    getAllWithDetails(withDelete?: boolean): angular.IPromise<any[]>;
    getAllUserModelsWithDetails(withDelete?: boolean): angular.IPromise<IUserModelDto[]>;
    getAllUserModelsWithDetails(force: boolean, withDelete?: boolean): angular.IPromise<IUserModelDto[]>;
    getUsersWithEMailAddress(): angular.IPromise<any[]>;
    getAllWithRoles(): angular.IPromise<UserDto[]>;
    getMe(): angular.IPromise<any>;
    getByUserName(userName: string): angular.IPromise<any>;
    getUserModelByUserName(userName: string): angular.IPromise<IUserModel>;
    addSelecteddDatesForUser(userName: string, data: DateRange): angular.IPromise<any>;
    getSelectedDatesForUser(userName: string): angular.IPromise<any>;
    addSavedDatesForUser(userName: string, data: DateRange): angular.IPromise<any>;
    updateSavedDatesForUser(userName: string, data: DateRange): angular.IPromise<any>;
    deleteSavedDatesForUser(userName: string, guid: string): angular.IPromise<any>;
    getSavedDatesForUser(userName: string): angular.IPromise<{ savedDates: DateRange[] }>;
    getLastSelectedGroup(): angular.IPromise<string>;
    saveLastSelectedGroup(groupGuid: string): angular.IPromise<any>;
    addSavedSearchForUser(userName: string, data: any, name: string, searchType?: string): angular.IPromise<any>;
    delSavedSearchForUser(userName: string, name: string, searchType?: string): angular.IPromise<any>;
    getSavedSearchForUser(userName: string, searchType?: string): angular.IPromise<any>;
    ////updateUser(user: any): angular.IPromise<any>;
    //getSelectedDatesForCurrentUser(): angular.IPromise<DateRange[]>;
    getSignature(userName: string): angular.IPromise<string>;
    updateUserSignature(file: File, userName: string): angular.IPromise<string>;
    deleteSignature(userName: string): angular.IPromise<void>;
    getPicture(userName: string): angular.IPromise<string>;
    getPictures(userIdList: string[]): angular.IPromise<IPictureDictionary>;
    updateUserPicture(file: File, userName: string): angular.IPromise<string>;
    deletePicture(userName: string): angular.IPromise<void>;
    mapUsers(users: IUserModelDto[], pictures?: IPictureDictionary): ApplicationUserViewModel[];
    getOnlineUsers(): angular.IPromise<OnlineUserDto[]>;
}

export class UserService implements IUserService {
    static serviceId = 'userService';

    static $inject = [
        'requestService',
        'uploadService'
    ];

    constructor(
        private _requestService: IRequestService,
        private _upload: IUploadService
    ) {
    }

    getAll(): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user';
        this._requestService.cleanCache(url);
        return this._requestService.get(url, undefined, true);
    }

    getAllWithDetails(withDelete = false): angular.IPromise<any[]> {
        const url = this._requestService.getUrlV1() + `user/details/${withDelete}`;
        this._requestService.cleanCache(url);
        return this._requestService.get(url, undefined, true);
    }

    getAllUserModelsWithDetails(withDelete?: boolean): angular.IPromise<IUserModelDto[]>;
    getAllUserModelsWithDetails(force: boolean, withDelete?: boolean): angular.IPromise<IUserModelDto[]>;
    getAllUserModelsWithDetails(force?: boolean, withDelete?: boolean): angular.IPromise<IUserModelDto[]> {
        const url = this._requestService.getUrlV1() + `user/userModelsDetails/${withDelete}`;
        if (force) {
            this._requestService.cleanCache(url);
        }
        return this._requestService.get(url, undefined, true);
    }

    getUsersWithEMailAddress(): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/getUsersWithEMailAddress';
        return this._requestService.get(url, undefined, true);
    }

    getAllWithRoles(): angular.IPromise<UserDto[]> {
        const url = this._requestService.getUrlV1() + 'user/getAllWithRoles';
        return this._requestService.get(url, undefined, true);
    }

    getMe(): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/me';
        return this._requestService.get(url, undefined, true);
    }

    getByUserName(userName: string): angular.IPromise<any> {
        // trailing slash is mandatory to prevent IIS to break on user with dot
        const url = `${this._requestService.getUrlV1()}user/${userName}/`;
        this._requestService.cleanCache(url);
        return this._requestService.get(url, undefined, true);
    }

    getUserModelByUserName(userName: string): angular.IPromise<IUserModel> {
        // trailing slash is mandatory to prevent IIS to break on user with dot
        const url = `${this._requestService.getUrlV1()}user/userModel/${userName}/`;
        this._requestService.cleanCache(url);
        return this._requestService.get(url, undefined, true);
    }

    addSelecteddDatesForUser(userName: string, data: DateRange): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/' + userName + '/settings/selectedDates';
        this._requestService.cleanCache(url);
        return this._requestService.post(url, data, undefined, true);
    }

    getSelectedDatesForUser(userName: string): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/' + userName + '/settings/selectedDates';
        return this._requestService.get(url, undefined, true);
    }

    addSavedDatesForUser(userName: string, data: DateRange): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/' + userName + '/settings/savedDates';
        this._requestService.cleanCache(url);
        return this._requestService.post(url, data, undefined, true);
    }

    updateSavedDatesForUser(userName: string, data: DateRange): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/' + userName + '/settings/updateSavedDates';
        return this._requestService.post(url, data, undefined, true)
            .then(data => {
                this._requestService.cleanCacheAtV1('user/' + userName + '/settings/savedDates');
                return data;
            });
    }

    deleteSavedDatesForUser(userName: string, guid: string): angular.IPromise<any> {
        const url = 'user/' + userName + '/settings/deleteSavedDates/' + guid;
        return this._requestService.postAtV1(url, null, undefined, true)
            .then(data => {
                this._requestService.cleanCacheAtV1('user/' + userName + '/settings/savedDates');
                return data;
            });
    }

    getSavedDatesForUser(userName: string): angular.IPromise<{ savedDates: DateRange[] }> {
        const url = this._requestService.getUrlV1() + 'user/' + userName + '/settings/savedDates';
        return this._requestService.get(url, undefined, true)
            .then((data: { savedDates: DateRange[] }) => {
                const sorted = _.sortBy(data.savedDates, [d => d.beginDateTime, d => d.endDateTime, d => d.comment?.toLowerCase()]);
                return { savedDates: sorted };
            });
    }

    getLastSelectedGroup(): angular.IPromise<string> {
        const url = this._requestService.getUrlV1() + 'user/lastSelectedGroup';
        return this._requestService.get(url, undefined, true);
    }

    saveLastSelectedGroup(groupGuid: string): angular.IPromise<any> {
        const url = this._requestService.getUrlV1() + 'user/saveSelectedGroup/' + groupGuid;
        return this._requestService.post(url, undefined, true);
    }

    addSavedSearchForUser(userName: string, search, name: string, searchType?: string): angular.IPromise<any> {
        const data = JSON.stringify(search);
        let url = this._requestService.getUrlV1() + `user/${userName}/settings/savedSearch`;
        this._requestService.cleanCache(url);
        url += `/${name}` + ((searchType) ? `/${searchType}` : '');
        this._requestService.cleanCache(url);
        return this._requestService.post(url, angular.toJson(data), undefined, true);
    }

    delSavedSearchForUser(userName: string, name: string, searchType?: string): angular.IPromise<any> {
        let url = this._requestService.getUrlV1() + 'user/' + userName + '/settings/savedSearch';
        this._requestService.cleanCache(url);
        url += `/${name}` + ((searchType) ? `/${searchType}` : '');
        this._requestService.cleanCache(url);
        return this._requestService.delete(url);
    }

    getSavedSearchForUser(userName: string, searchType?: string): angular.IPromise<KeyValuePair<string, string>[]> {
        const url = this._requestService.getUrlV1() + `user/${userName}/settings/savedSearch`
            + ((searchType) ? `/${searchType}` : '');

        return this._requestService.get(url, undefined, true)
            .then(data => {
                _.forEach(data, (value, key) => {
                    if (value) {
                        data[key] = JSON.parse(<string>value);
                    }
                });
                return data;
            });
    }

    getSignature(userName: string): angular.IPromise<string> {
        this._requestService.cleanCacheAtV1(`user/getSignature/?userName=${userName}`);
        return this._requestService.getAtV1('user/getSignature/', { userName: userName }, true);
    }

    updateUserSignature(file: File, userName: string): angular.IPromise<string> {
        const url = `${this._requestService.getUrlV1()}user/updateUserSignature/?userName=${userName}`;
        return this._upload
            .uploadFile<string>(url, file)
            .then(data => {
                this._requestService.cleanCacheAtV1(`user/getSignature/?userName=${userName}`);
                return data;
            });
    }

    deleteSignature(userName: string): angular.IPromise<void> {
        this._requestService.cleanCacheAtV1(`user/getSignature/?userName=${userName}`);
        return this._requestService.postAtV1<void>(`user/deleteSignature?userName=${userName}`, undefined, true);
    }

    getPicture(userName: string): angular.IPromise<string> {
        return this._requestService.getAtV1('user/getPictureByUsername/', { userName: userName }, true);
    }

    getPictures(userIdList: string[]): angular.IPromise<IPictureDictionary> {
        const data = JSON.stringify(userIdList);
        return this._requestService.postAtV1<IPictureDictionary>('user/getPictures', data, undefined, true);
    }

    updateUserPicture(file: File, userName: string): angular.IPromise<string> {
        const url = `${this._requestService.getUrlV1()}user/updateUserPictureByUsername/?userName=${userName}`;
        return this._upload
            .uploadFile<string>(url, file)
            .then(data => {
                this._requestService.cleanCacheAtV1(`user/getPictureByUsername/?userName=${userName}`);
                return data;
            });
    }

    deletePicture(userName: string): angular.IPromise<void> {
        this._requestService.cleanCacheAtV1(`user/getPictureByUsername/?userName=${userName}`);
        return this._requestService.postAtV1<void>(`user/deletePictureByUsername?userName=${userName}`, undefined, true);
    }

    mapUsers(users: IUserModelDto[], pictures?: IPictureDictionary): ApplicationUserViewModel[] {
        return users.map(user => {
            return {
                name: user.userName,
                userId: user.guid,
                guid: user.guid,
                roles: user.roles,
                title: user.title,
                firstName: user.firstName,
                lastName: user.lastName,
                company: user.company,
                initials: user.initials,
                displayName: user.displayName,
                phoneNumber: user.phoneNumber,
                email: user.email,
                picture: (pictures && user.guid in pictures && pictures[user.guid]) ? 'data:image/png;base64,' + pictures[user.guid] : undefined,
                datePickerFormat: user.datePickerFormat
            } as ApplicationUserViewModel;
        });
    }

    getOnlineUsers(): angular.IPromise<OnlineUserDto[]> {
        return this._requestService.getAtV1(`user/onlineUsers/`, undefined, true);
    }
}